import { useState, useEffect, useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";
import hooks from "../../node_modules/moment/dist/moment.js";
import { formatDate } from "../../utils/date.js";
import { isDateDisabled } from "./utils.js";
const localized = defineMessages({
  weekValidationMessage: {
    id: "DateRangePicker_WeekValidationMessage",
    defaultMessage: "Please select start and end of the week"
  },
  beforeValidationMessage: {
    id: "DateRangePicker_beforeValidationMessage",
    defaultMessage: "Please select a date range before {date}."
  },
  afterValidationMessage: {
    id: "DateRangePicker_afterValidationMessage",
    defaultMessage: "Please select a date range from {date}."
  },
  datePickerYearMonthFormJanuary: {
    id: "datePickerYearMonthFormJanuary",
    defaultMessage: "January"
  },
  datePickerYearMonthFormFebruary: {
    id: "datePickerYearMonthFormFebruary",
    defaultMessage: "February"
  },
  datePickerYearMonthFormMarch: {
    id: "datePickerYearMonthFormMarch",
    defaultMessage: "March"
  },
  datePickerYearMonthFormApril: {
    id: "datePickerYearMonthFormApril",
    defaultMessage: "April"
  },
  datePickerYearMonthFormMay: {
    id: "datePickerYearMonthFormMay",
    defaultMessage: "May"
  },
  datePickerYearMonthFormJune: {
    id: "datePickerYearMonthFormJune",
    defaultMessage: "June"
  },
  datePickerYearMonthFormJuly: {
    id: "datePickerYearMonthFormJuly",
    defaultMessage: "July"
  },
  datePickerYearMonthFormAugust: {
    id: "datePickerYearMonthFormAugust",
    defaultMessage: "August"
  },
  datePickerYearMonthFormSeptember: {
    id: "datePickerYearMonthFormSeptember",
    defaultMessage: "September"
  },
  datePickerYearMonthFormOctober: {
    id: "datePickerYearMonthFormOctober",
    defaultMessage: "October"
  },
  datePickerYearMonthFormNovember: {
    id: "datePickerYearMonthFormNovember",
    defaultMessage: "November"
  },
  datePickerYearMonthFormDecember: {
    id: "datePickerYearMonthFormDecember",
    defaultMessage: "December"
  }
});
function useDateRangePicker({
  value,
  rangeMode = "default",
  availableDateRangeForSelection,
  errorMessage,
  onChange,
  onSelect
}) {
  const [selectedDateRange, setSelectedDateRange] = useState(value);
  useEffect(() => {
    setSelectedDateRange(value);
  }, [`${value?.from}${value?.to}`]);
  const isDateSelected = value?.from !== null && value?.from !== void 0 || value?.to !== null && value?.to !== void 0;
  const isWeekMode = rangeMode === "weeks";
  function isMonday(date) {
    return date.getDay() === 1;
  }
  const error = useMemo(() => {
    let resultMessage = isWeekMode ? localized.weekValidationMessage : void 0;
    let resultValues = {};
    if (availableDateRangeForSelection?.from && (value?.from < availableDateRangeForSelection?.from || value?.to < availableDateRangeForSelection?.from)) {
      resultMessage = errorMessage?.afterNotValid?.label ?? localized.afterValidationMessage;
      resultValues = {
        date: formatDate(availableDateRangeForSelection?.from, "date", false),
        ...errorMessage?.afterNotValid?.labelParams ?? {}
      };
    }
    if (availableDateRangeForSelection?.to && (value?.from > availableDateRangeForSelection?.to || value?.to > availableDateRangeForSelection?.to)) {
      resultMessage = errorMessage?.beforeNotValid?.label ?? localized.beforeValidationMessage;
      resultValues = {
        date: formatDate(availableDateRangeForSelection?.to, "date", false),
        ...errorMessage?.beforeNotValid?.labelParams ?? {}
      };
    }
    return {
      message: resultMessage,
      values: resultValues
    };
  }, [
    isWeekMode,
    value?.from?.toDateString(),
    value?.to?.toDateString(),
    availableDateRangeForSelection?.from?.toDateString(),
    availableDateRangeForSelection?.to?.toDateString()
  ]);
  function isSunday(date) {
    return date.getDay() === 0;
  }
  function isDateInWeekRange(date) {
    let result = true;
    if (isWeekMode) {
      const isRangeSelected = selectedDateRange?.from && selectedDateRange?.to && selectedDateRange.from.getTime() !== selectedDateRange.to.getTime();
      const isMondayDateSelected = selectedDateRange?.from && isMonday(selectedDateRange.from);
      const isSundayDateSelected = selectedDateRange?.to && isSunday(selectedDateRange.to);
      result = isMonday(date) || isSunday(date);
      if (isRangeSelected) {
        result = date.getTime() === selectedDateRange.from.getTime() || date.getTime() === selectedDateRange.to.getTime();
      } else if (isMondayDateSelected) {
        result = isSunday(date) && date.getTime() > selectedDateRange.from.getTime();
      } else if (isSundayDateSelected) {
        result = isMonday(date) && date.getTime() < selectedDateRange.to.getTime();
      }
    }
    return result;
  }
  function isDateDisabled$1(date) {
    return !isDateInWeekRange(date) || isDateDisabled(date, availableDateRangeForSelection);
  }
  function getValueIsValid(value2) {
    let result = true;
    if (isWeekMode && (!value2?.from || !value2?.to || value2?.from?.getTime() === value2?.to?.getTime() || !isMonday(value2.from) || !isSunday(value2.to))) {
      result = false;
    }
    result = result && !isDateDisabled$1(value2?.from) && !isDateDisabled$1(value2?.to);
    return result;
  }
  const isValid = getValueIsValid(selectedDateRange);
  function handleSelect(dateRange) {
    setSelectedDateRange(dateRange ?? { from: null, to: null });
    if (onSelect) {
      onSelect(dateRange);
    }
  }
  function handleCancelClick() {
    const clearValue = {};
    setSelectedDateRange(clearValue);
    onChange(clearValue);
  }
  function handleApplyClick() {
    if (isValid) {
      onChange(selectedDateRange);
    }
  }
  function handleOutsideClick() {
    setSelectedDateRange(value);
  }
  return {
    isDateSelected,
    isValid,
    selectedDateRange,
    error,
    isDateDisabled: isDateDisabled$1,
    handleSelect,
    handleApplyClick,
    handleCancelClick,
    handleOutsideClick
  };
}
const months = [
  localized.datePickerYearMonthFormJanuary,
  localized.datePickerYearMonthFormFebruary,
  localized.datePickerYearMonthFormMarch,
  localized.datePickerYearMonthFormApril,
  localized.datePickerYearMonthFormMay,
  localized.datePickerYearMonthFormJune,
  localized.datePickerYearMonthFormJuly,
  localized.datePickerYearMonthFormAugust,
  localized.datePickerYearMonthFormSeptember,
  localized.datePickerYearMonthFormOctober,
  localized.datePickerYearMonthFormNovember,
  localized.datePickerYearMonthFormDecember
];
const minAvailableYear = 1900;
const maxAvailableYearFromToday = 100;
function useYearsAndMonthsOptions({ disableFutureDates = false }) {
  const { formatMessage } = useIntl();
  const maxDate = disableFutureDates ? hooks().toDate() : hooks().add(maxAvailableYearFromToday, "years").toDate();
  const monthsOptions = months.map((m, index) => ({ label: formatMessage(m), value: index }));
  const yearsOptions = getYearsOptions();
  function getYearsOptions() {
    const yearsOptions2 = [];
    const maxAvailableYear = maxDate.getFullYear();
    for (let index = minAvailableYear; index <= maxAvailableYear; index += 1) {
      yearsOptions2.push({ label: `${index}`, value: index });
    }
    return yearsOptions2;
  }
  return { yearsOptions, monthsOptions };
}
export {
  localized,
  useDateRangePicker,
  useYearsAndMonthsOptions
};
//# sourceMappingURL=hooks.js.map
